import React from "react";
import PropTypes from "prop-types";
import { useIntl, FormattedMessage } from "react-intl";
import { Row, Col, Form, Button } from "react-bootstrap";
import { Plus, Dash, Trash, CaretUp, CaretDown } from "react-bootstrap-icons";
import { v4 as uuidv4 } from "uuid";

export const TemplateField = ({
  index,
  rowNumber,
  field,
  readOnly,
  handleUpdateFieldValue,
  handleAddField,
  handleDelete,
  handleMoveUp,
  handleMoveDown,
}) => {
  const intl = useIntl();

  return (
    <Form.Group className="p-2 mb-3 border border-2 border-primary">
      <div className="d-flex justify-content-between border-bottom border-primary mb-2 pb-2">
        <span>{rowNumber + 1}.</span>
        <Form.Check
          type="checkbox"
          className="mb-2"
          id={`template-field-checkbox-required-${index}`}
          label={intl.formatMessage({
            id: "data.template.form.field.required",
          })}
          defaultChecked={field?.required}
          disabled={readOnly}
          onChange={(event) => {
            handleUpdateFieldValue("required", event.target.checked);
          }}
        />
      </div>
      <Row>
        <Col lg={11}>
          <Form.Control
            type="text"
            className="mb-2"
            placeholder={intl.formatMessage({
              id: "data.template.form.field.name.placeholder",
            })}
            defaultValue={field?.name}
            required
            readOnly={readOnly}
            onChange={(event) => {
              handleUpdateFieldValue("name", event.target.value);
            }}
          />
          <Form.Control
            type="text"
            className="mb-2"
            placeholder={intl.formatMessage({
              id: "data.template.form.field.worksheetTitle.placeholder",
            })}
            defaultValue={field?.worksheetTitle}
            required
            readOnly={readOnly}
            onChange={(event) => {
              handleUpdateFieldValue("worksheetTitle", event.target.value);
            }}
          />
          <Form.Control
            type="text"
            className="mb-2"
            placeholder={intl.formatMessage({
              id: "data.template.form.field.description.placeholder",
            })}
            defaultValue={field?.description}
            as="textarea"
            rows={3}
            readOnly={readOnly}
            onChange={(event) => {
              handleUpdateFieldValue("description", event.target.value);
            }}
          />
          <div className="d-flex">
            <Form.Check
              type="checkbox"
              className="me-2"
              id={`template-field-checkbox-photos-${index}`}
              label={intl.formatMessage({
                id: "data.template.form.field.photos",
              })}
              disabled={readOnly}
              defaultChecked={field?.photos}
              onChange={(event) => {
                handleUpdateFieldValue("photos", event.target.checked);
              }}
            />
            <Form.Check
              type="checkbox"
              id={`template-field-checkbox-text-${index}`}
              label={intl.formatMessage({
                id: "data.template.form.field.text",
              })}
              disabled={readOnly}
              defaultChecked={field?.text}
              onChange={(event) => {
                handleUpdateFieldValue("text", event.target.checked);
              }}
            />
          </div>
          {field?.text ? (
            <div>
              <div className="mt-2 me-2">
                <FormattedMessage id="data.template.data.fieldoptions" />:
              </div>
              <div>
                {field.options.map((option, index) => (
                  <div key={option.uuid} className="d-flex mb-2">
                    <Form.Control
                      type="text"
                      className="me-2"
                      placeholder={intl.formatMessage({
                        id: "data.template.data.fieldoptions",
                      })}
                      defaultValue={option.value}
                      readOnly={readOnly}
                      onChange={(event) => {
                        const newArray = [
                          ...field.options.slice(0, index),
                          {
                            uuid: option.uuid,
                            value: event.target.value,
                          },
                          ...field.options.slice(
                            index + 1,
                            field.options.length,
                          ),
                        ];
                        handleUpdateFieldValue("options", newArray);
                      }}
                    />
                    <Button
                      variant="outline-primary"
                      onClick={() => {
                        const newArray = [
                          ...field.options.slice(0, index),
                          ...field.options.slice(
                            index + 1,
                            field.options.length,
                          ),
                        ];
                        handleUpdateFieldValue("options", newArray);
                      }}
                    >
                      <Dash />
                    </Button>
                  </div>
                ))}
              </div>
              <div className="text-end">
                <Button
                  variant="outline-primary"
                  onClick={() => {
                    handleUpdateFieldValue("options", [
                      ...field.options,
                      {
                        uuid: uuidv4(),
                        value: "",
                      },
                    ]);
                  }}
                >
                  <Plus />
                </Button>
              </div>
            </div>
          ) : null}
        </Col>
        <Col lg={1}>
          {!readOnly ? (
            <>
              <div className="mb-2 text-end">
                <Button variant="outline-primary" onClick={handleAddField}>
                  <Plus />
                </Button>
              </div>
              <div className="mb-2 text-end">
                <Button variant="outline-danger" onClick={handleDelete}>
                  <Trash />
                </Button>
              </div>
              <div className="mb-2 text-end">
                <Button variant="outline-secondary" onClick={handleMoveUp}>
                  <CaretUp />
                </Button>
              </div>
              <div className="text-end">
                <Button variant="outline-secondary" onClick={handleMoveDown}>
                  <CaretDown />
                </Button>
              </div>
            </>
          ) : null}
        </Col>
      </Row>
    </Form.Group>
  );
};

TemplateField.propTypes = {
  index: PropTypes.string.isRequired,
  rowNumber: PropTypes.number.isRequired,
  field: PropTypes.object.isRequired,
  readOnly: PropTypes.bool.isRequired,
  handleUpdateFieldValue: PropTypes.func.isRequired,
  handleAddField: PropTypes.func.isRequired,
  handleDelete: PropTypes.func.isRequired,
  handleMoveUp: PropTypes.func.isRequired,
  handleMoveDown: PropTypes.func.isRequired,
};
